/* eslint-disable max-lines */

import { A } from '@adornis/base/env-info.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/datetime/x-date-picker-dropdown.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/popover/x-tooltip.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { FormOfFinancing } from '@chancenwerk/_cms/financing/entities/FormOfFinancing.js';
import { Adresse, ZipCity } from '@chancenwerk/db/adresse.js';
import { Subject } from '@chancenwerk/db/courses/CourseDigital.js';
import { Bundesland, Mitgliedschaften, StudentType } from '@chancenwerk/db/enums/enums.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { SemanticRelationship } from '@chancenwerk/db/semantics/SemanticRelationship.js';
import { StudentParticipationRelationship } from '@chancenwerk/db/semantics/StudentParticipationRelationship.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import { type ChromiumFlex } from '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-loader.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-toggle-button.js';
import '@chancenwerk/design/components/fonts.js';
import { FormOfFinancingLogicExpression } from '@chancenwerk/master-data-management/kpf/entities/FormOfFinancingLogicExpression.js';
import type { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import type { School } from '@chancenwerk/master-data-management/partner/entities/School.js';
import {
  GraduationInformation,
  SchoolInformation,
  Student,
  StudentTags,
} from '@chancenwerk/master-data-management/users/entities/Student.js';
import { KPFPublicInformationAPI } from '@chancenwerk/public-access/api/KPFPublicInformationAPI.js';
import { PartnerPublicInformationAPI } from '@chancenwerk/public-access/api/PartnerPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { from, switchMap } from 'rxjs';
import '../../../../src/print-components/chancen-component-anmeldung-consent.js';
import '../components/consent-texts/anmeldung-component-formfield-consent-eval.js';
import '../components/consent-texts/anmeldung-component-formfield-consent-picture.js';
import '../cr-cta-text.js';
import AbschlussLogo from './../assets/chancenabschluss_logo.png';
import './anmeldung-component-footer.js';
import './anmeldung-component-formular-attendance.js';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';
import './anmeldung-component-formular-but.js';
import './anmeldung-component-formular-data-overview.js';
import './anmeldung-component-formular-data-validation.js';
import './anmeldung-component-formular-digital-courses.js';
import './anmeldung-component-formular-finished.js';
import './anmeldung-component-formular-graduation-subjects.js';
import './anmeldung-component-formular-lang-select.js';
import './anmeldung-component-formular-lkk-us.js';
import './anmeldung-component-formular-lkk.js';
import './anmeldung-component-formular-personal-info.js';
import './anmeldung-component-formular-school-info.js';
import './anmeldung-component-formular-support.js';

/**
 * Defines the starting step of the formular. This is only applied in a dev environment.
 */
const DEVELOPMENT_STARTING_STEP = 1;

/**
 * Will insert some basic personal info into the student for faster access to later pages.
 */
const DEVELOPMENT_INSERT_MOCK_DATA = true;

const formOfFionanzingSelector: BaseQLSelectionSet<FormOfFinancingLogicExpression | FormOfFinancing> = {
  ...FormOfFinancingLogicExpression.allFields,
  ...FormOfFinancing.allFields,
};

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _class: 1,
  _id: 1,
  endDatum: 1,
  email: 1,
  produkt: {
    // @ts-ignore Only exists on a special product
    durchfuehrungsTage: {
      _class: 1,
      wochentag: 1,
      startZeit: 1,
      endZeit: 1,
      locations: 1,
      cid: 1,
    },
  },
  schoolID: 1,
  school: {
    _id: 1,
    _class: 1,
    name: 1,
    address: Adresse.allFields,
    bundesland: 1,
  },
  standardMembershipFee: 1,
  formOfFinanzingLogicString: formOfFionanzingSelector,
};

/**
 * @customElement anmeldung-component-formular-graduation
 */
@customElement('anmeldung-component-formular-graduation')
export class AnmeldungComponentFormularGraduation extends AnmeldungPageRegistrationBase {
  @property({ type: Number, attribute: false }) step = new RXController(
    this,
    A.isDevelopment ? DEVELOPMENT_STARTING_STEP : 1,
  ); // ! Dont change the step here, use the const at the start

  @state() private readonly _student = new RXController(
    this,
    new Student({
      username: 'new',
      schuelertyp: StudentType.OS,
      tags: [StudentTags.FROM_ABSCHLUSS],
      mitgliedschaft: Mitgliedschaften.HILFE_NEHMEN,
      //@ts-expect-error Date is not DateTime
      schoolInformation: new SchoolInformation({
        jahrgangsstufe: null,
      }),
      graduationInformation: new GraduationInformation({
        daysAvailable: [],
      }),
    }),
  );

  @state() private readonly _kpfs = new RXController(
    this,
    from(KPFPublicInformationAPI.getAll()(kpfSelector)).pipe(
      switchMap(async kpfs => {
        kpfs = kpfs.filter(kpf => {
          if (kpf.produkt?._class !== 'ProductABS') return false;
          if (!kpf.endDatum) return true;
          return kpf.endDatum.toMillis() >= DateTime.now().toMillis();
        });

        const partners = await PartnerPublicInformationAPI.getAllSchool()({
          name: 1,
          address: Adresse.allFields,
          bundesland: 1,
        });

        for (const kpf of kpfs) {
          kpf.school = constructValue(partners.find(p => p._id === kpf.schoolID)) as Partner;
        }

        return kpfs;
      }),
    ),
  );

  @state() private readonly _translation = new TranslationController(this);
  @state() private readonly _size = new WindowSizeController(this);
  @state() private readonly _choosenKPF = new RXController<KPF | undefined>(this, undefined);
  @state() private readonly _schoolCityForFilter = new RXController<ZipCity | undefined>(this, new ZipCity({}));

  private readonly _legalGuardian1AddressDifferent = new RXController(this, false);
  private readonly _legalGuardian2AddressDifferent = new RXController(this, false);
  private readonly _oldSchoolCityForFilter: ZipCity | undefined = undefined;
  private readonly _formHeadlineEl = createRef<ChromiumFlex>();

  @state() private readonly _form = new FormController(this, this._student.observable, {
    onSuccess: async () => {
      const student = this._student.value;
      [
        null,
        this._processStep1.bind(this),
        this._processStep2.bind(this),
        this._processStep3.bind(this),
        this._processStep4.bind(this),
      ][this.step.value]?.(student);
    },
    success: () => {
      requestAnimationFrame(() =>
        this._formHeadlineEl.value?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' }),
      );
    },
    error: err => {
      if (err instanceof MultipleValidationError)
        return ChromiumSnackbar.show(err.extensions.validationErrors?.[0]?.message ?? err.message);
      return ChromiumSnackbar.show(err.message);
    },
    validationMeta: () => ({
      step: this.step.value,
    }),
  });

  override connectedCallback(): void {
    super.connectedCallback();

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (A.isDevelopment && DEVELOPMENT_INSERT_MOCK_DATA) {
      this._student.value.vorname = 'Max';
      this._student.value.nachname = 'Musterman';
      this._student.value.email = `krumm-felix+${A.getGloballyUniqueID()}@adornis.de`;
      if (!this._student.value.adresse) this._student.value.adresse = new Adresse({});
      if (!this._student.value.adresse.zipCity) this._student.value.adresse.zipCity = new ZipCity({});
      this._student.value.adresse.strasse = 'Test Str. 123';
      this._student.value.staatsangehoerigkeit = 'DE';
      this._student.value.spokenLanguage = 'Deutsch';
      this._student.value.adresse.zipCity.zip = '64297';
      this._student.value.adresse.zipCity.city = 'Darmstadt';
      this._student.value.handynummer = '+49111111111';
      this._student.value.geburtstag = DateTime.now().minus({ years: 10 });

      this._student.value.legalGuardian1.vorname = 'Max';
      this._student.value.legalGuardian1.nachname = 'Mustermutter';
      this._student.value.legalGuardian1.email = this._student.value.email;
      this._student.value.legalGuardian1.handynummer = '+49111111111';
    }

    this._form.document?.whenChanging.subscribe(() => {
      this.requestUpdate();
    });
  }

  public override render() {
    if (this._size.value?.mobile) return this._content();

    return html` <cr-section> ${this._content()} </cr-section> `;
  }

  private _content() {
    return html`
      <cr-flex space=${this._size.mobile ? 'lg' : 'xl'}>
        <cr-flex crossaxis-center flex h100 space=${this._size.mobile ? 'lg' : 'xl'} ${css({ overflow: 'auto' })}>
          <cr-flex class="headline" crossaxis-start end>
            <cr-flex class="h-content" space="lg" padding="lg">
              <cr-h1 ${ref(this._formHeadlineEl)}> Anmeldung zum CHANCENabschluss </cr-h1>

              ${this.step.value <= 7
                ? html`
                    <cr-flex horizontal crossaxis-center space="lg" class="tablet--flex form-headline">
                      ${this.step.value >= 2
                        ? html` <cr-button @click=${() => this._nextDown()}>
                            ${this._translation.translate('back')}
                          </cr-button>`
                        : nothing}

                      <cr-h2
                        ${css({
                          lineHeight: this.spacing.lg,
                          color: this.colors.neutral.shade(900),
                        })}
                      >
                        ${this._getStepTitle()}
                      </cr-h2>
                    </cr-flex>
                  `
                : nothing}
            </cr-flex>
          </cr-flex>

          ${this.step.value === 1
            ? html`<cr-h4 align="center" ${css({ padding: `0 ${this.spacing.sm}` })}>
                <b> Hiermit melde ich mich zum CHANCENabschluss von Chancenwerk e.V. an.</b>
              </cr-h4>`
            : nothing}

          <cr-flex class="form-content-page" space="xl" crossaxis-center h100 w100>
            ${[
              () => nothing,
              this._getSchoolInformationSection.bind(this),
              this._getPersonalInformationSection.bind(this),
              this._getSubjectsSection.bind(this),
              this._getDataValidation.bind(this),
              this._getFinishedScreen.bind(this),
            ][this.step.value]?.() ?? nothing}

            <anmeldung-component-footer
              ${css({
                flex: 'auto',
                paddingBottom: this.spacing.xl,
              })}
              .schoolLevel=${'os'}
              ?show-required=${true}
            ></anmeldung-component-footer>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private async _processStep1(student: Student) {
    if (!this._choosenKPF.value) {
      const errMsg = 'Eine Schule muss ausgewählt sein!';
      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg);
    }

    if (!student.schoolInformation.jahrgangsstufe) {
      const errMsg = 'Eine Jahrgangsstufe muss ausgewählt sein!';
      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg, {
        path: ['schoolInformation'],
        key: 'jahrgangsstufe',
        translationKey: 'errorJahrgangsstufe',
        source: student,
      });
    }

    if (!student.graduationInformation?.type) {
      const errMsg = 'Ein Abschluss muss ausgewählt sein!';
      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg, {
        path: ['graduationInformation'],
        key: 'type',
        translationKey: 'errorAbschluss',
        source: student,
      });
    }

    this._nextUp();
  }

  // eslint-disable-next-line complexity
  private async _processStep2(student: Student) {
    if (!student.email) {
      throw new ValidationError(`Bitte gebe deine E-Mail an!`, {
        key: 'email',
        source: student,
        translationKey: 'errorEmail',
      });
    }

    if (!student.handynummer) {
      throw new ValidationError(`Bitte gebe deine Handynummer an!`, {
        key: 'handynummer',
        source: student,
        translationKey: 'errorHandynummer',
      });
    }

    // if (!student.geburtstag) {
    //   throw new ValidationError(`Bitte gebe deinen Geburtstag an!`, {
    //     key: 'geburtstag',
    //     source: student,
    //     translationKey: 'errorGeburtstagOs',
    //   });
    // }

    if (!student.adresse?.strasse) {
      throw new ValidationError(`Die Adresse muss vollständig angegeben werden! Straße fehlt...`, {
        path: ['adresse'],
        key: 'strasse',
        source: student,
        translationKey: 'errorStrasse',
      });
    }

    if (!/\d/.test(student.adresse.strasse)) {
      if (
        !(await ChromiumDialog.confirm(
          `Ihre Straße '${student.adresse.strasse}' enthält keine Hausnummer. Ist das wirklich korrekt?`,
        ))
      ) {
        throw new ValidationError(`Die Adresse muss vollständig angegeben werden!`, {
          path: ['adresse'],
          key: 'strasse',
          source: student,
          translationKey: 'errorStrasse',
        });
      }
    }

    if (!student.adresse?.zipCity?.zip?.trim().length || !student.adresse.zipCity.city?.trim().length) {
      throw new ValidationError(`Die Adresse muss vollständig angegeben werden! Stadt fehlt...`, {
        path: ['adresse'],
        key: 'zipCity',
        source: student,
        translationKey: 'errorStadt',
      });
    }

    await this._addressConfirm(student);
    this._nextUp();
  }

  private async _processStep3(student: Student) {
    if (!student.graduationInformation?.firstSubjectGraduation) {
      ChromiumSnackbar.show(`Bitte wähle mindestens ein Fach aus!`);
      throw new ValidationError(`Bitte wähle mindestens ein Fach aus!`, {
        key: 'firstSubjectGraduation',
        source: student,
        translationKey: 'errorSubject',
      });
    }

    if (
      !student.graduationInformation?.daysAvailable?.length &&
      ![9, 10].includes(student.schoolInformation.jahrgangsstufe ?? 0)
    ) {
      ChromiumSnackbar.show(`Bitte wähle mindestens einen Wochentag aus!`);
      throw new ValidationError(`Bitte wähle mindestens einen Wochentag aus!`, {
        key: 'daysAvailable',
        source: student,
        translationKey: 'errorDay',
      });
    }

    this._nextUp();
  }

  private async _addressConfirm(student: Student) {
    const emailConfirm = html`
      <cr-flex space="sm" padding="md">
        <cr-h3>Stimmen deine angegebenen Kontaktdaten?</cr-h3>

        <cr-grid columns="auto auto" space="md" padding="md">
          <cr-h4> E-Mail: </cr-h4>
          <cr-text> ${student.email} </cr-text>

          <cr-h4> Handynummer: </cr-h4>
          <cr-text> ${student.handynummer} </cr-text>
        </cr-grid>
      </cr-flex>
    `;
    if (!(await XDialog.confirm(emailConfirm)))
      throw new ValidationError('Adresse wurde als nicht korrekt gekennzeichnet!', {
        path: ['legalGuardian1'],
        key: 'adresse',
        source: this._student.value,
        translationKey: 'errorAddressIncorect',
      });
  }

  private async _processStep4(student: Student) {
    await XDialog.waitFor(
      // eslint-disable-next-line complexity
      new Promise<void>(async (resolve, reject) => {
        if (!this._student.value.graduationInformation) {
          throw new Error('Graduation information is missing');
        }

        this._student.value.joinInformation.eintrittsdatum = DateTime.now();

        if (!this._legalGuardian1AddressDifferent.value) {
          this._student.value.legalGuardian1.adresse = new Adresse({});
        }
        if (!this._legalGuardian2AddressDifferent.value) {
          this._student.value.legalGuardian2.adresse = new Adresse({});
        }

        this._student.value.consent.einverstaendnisEvaluation = true;
        this._student.value.schuelertyp = StudentType.OS;

        try {
          // Register student
          const newStudent = await Student.createStudent(this._student.value)({
            studentID: 1,
          });
          this._student.value._id = newStudent._id;
          this._student.value.studentID = newStudent.studentID;

          // Build relationship to kpf
          const rel = new StudentParticipationRelationship({
            subjectID: newStudent._id,
            predicateID: Role.STUDENT,
            objectID: this._choosenKPF.value?._id,
          });

          await SemanticRelationship.saveSemanticRelationshipsAndUpdateRoles([rel])();
          await Student.createDebitorennummer(newStudent)();
          await Student.confirmGraduationRegistration(this._student.value)();

          resolve();
        } catch (e: any) {
          const errMsg = 'Da ist etwas schiefgegangen.\n' + e.message;

          await XDialog.alert(errMsg);
          return reject(errMsg);
        }
      }),
      'Registrierung wird abgeschlossen.',
    );
    this._nextUp();
  }

  private _graduationType() {
    switch (this._choosenKPF.value?.school?.bundesland) {
      case Bundesland.SAARLAND:
        return (
          {
            9: 'Hauptschulabschluss',
            10: 'Mittlerer Bildungsabschluss',
            11: 'EF',
            12: 'Abitur',
            13: 'Abitur',
          }[this._student.value.schoolInformation.jahrgangsstufe ?? 0] ?? ''
        );
      case Bundesland.NORDRHEINWESTFALEN:
        return (
          {
            9: 'der Erste Schulabschluss (vormals Hauptschulabschluss)',
            10: [
              'der Erweiterte Erste Schulabschluss (vormals Hauptschulabschluss nach Klasse 10) (ZAP)',
              'der Mittlere Schulabschluss (Fachoberschulreife) (ZAP)',
            ],
            11: 'EF',
            12: 'Abitur',
            13: 'Abitur',
          }[this._student.value.schoolInformation.jahrgangsstufe ?? 0] ?? ''
        );
      case Bundesland.BADENWUERTTEMBERG:
        return (
          {
            9: 'Hauptschulabschluss',
            10: ['Hauptschulabschluss', 'Der mittlere Bildungsabschluss'],
            12: 'Abitur',
            13: 'Abitur',
          }[this._student.value.schoolInformation.jahrgangsstufe ?? 0] ?? ''
        );
      case Bundesland.HAMBURG:
        return (
          {
            9: 'Erster Bildungsabschluss',
            10: 'Mittlerer Bildungsabschluss',
            12: 'Abitur auf dem Gymnasium',
            13: 'Abitur auf der Stadtteilschule',
          }[this._student.value.schoolInformation.jahrgangsstufe ?? 0] ?? ''
        );
      case Bundesland.RHEINLANDPFALZ:
        return (
          {
            9: 'Berufsreife',
            10: 'Qualifizierter Sekundarabschluss',
            12: 'Abitur',
            13: 'Abitur',
          }[this._student.value.schoolInformation.jahrgangsstufe ?? 0] ?? ''
        );
      default:
        return (
          {
            9: 'Hauptschulabschluss',
            10: ['Hauptschulabschluss', 'Mittlere Reife'],
            11: 'Fachhochschulreife',
            12: ['Fachhochschulreife', 'Abitur'],
            13: 'Abitur',
          }[this._student.value.schoolInformation.jahrgangsstufe ?? 0] ?? ''
        );
    }
  }

  private _subjectWishes() {
    let choices: string[] = [];

    if (this._student.value.schoolInformation.jahrgangsstufe === 9) {
      choices = [Subject.MATHE, Subject.DEUTSCH];
    }

    if (this._student.value.schoolInformation.jahrgangsstufe ?? 0 > 9) {
      choices = [Subject.MATHE, Subject.DEUTSCH, Subject.ENGLISH];
    }

    if (this._student.value.graduationInformation?.type === 'Abitur') {
      choices = choices.flatMap(c => [`${c} (GK)`, `${c} (LK)`]);
    }

    return choices;
  }

  private _filterSchools() {
    return (
      this._kpfs.value
        ?.sort((a, b) => a.school?.name?.toLowerCase().localeCompare(b.school?.name?.toLowerCase() || 'z') ?? -1)
        .filter(kpf => {
          if (this._schoolCityForFilter.value?.city) {
            if ((kpf.school as School).address?.zipCity?.city !== this._schoolCityForFilter.value.city) return false;
            return true;
          }
          return true;
        })
        .map(p => p._id) ?? []
    );
  }

  private _graduationTypeSelection() {
    const type = this._graduationType();
    if (type === '' || !type) {
      return nothing;
    }

    const hasChoice = Array.isArray(type);
    const choice = hasChoice
      ? html`<cr-dropdown-selection
          placeholder=${'Wähle deinen Abschluss aus'}
          .selectables=${type}
          .value=${this._student.value.graduationInformation?.type}
          @value-picked=${(e: ValueEvent<string>) => {
            if (!this._student.value.graduationInformation) {
              return;
            }
            this._student.value.graduationInformation.type = e.detail.value;
          }}
        ></cr-dropdown-selection>`
      : nothing;
    const text = html`<cr-text ${css({ fontSize: '1.2rem', marginTop: this.spacing.md, textAlign: 'left' })}>
      Du meldest dich für <b>${!hasChoice ? type : this._student.value.graduationInformation?.type}</b> an
    </cr-text>`;

    if (
      !hasChoice &&
      this._student.value.graduationInformation &&
      this._student.value.graduationInformation.type !== type
    ) {
      this._student.value.graduationInformation.type = type;
      this._student.next(this._student.value);
    }

    if (
      hasChoice &&
      this._student.value.graduationInformation?.type &&
      !type.includes(this._student.value.graduationInformation.type)
    ) {
      this._student.value.graduationInformation.type = null;
      this._student.next(this._student.value);
    }

    return html`
      ${choice} ${!hasChoice || (hasChoice && this._student.value.graduationInformation?.type) ? text : nothing}
    `;
  }

  private _getSchoolInformationSection() {
    const schools = this._filterSchools();
    return html`<anmeldung-component-formular-school-info
      .schoolLevel=${'os'}
      .form=${this._form}
      .translation=${this._translation}
      .schools=${schools}
      .oldSchoolCityForFilter=${this._oldSchoolCityForFilter}
      .schoolCityForFilter=${this._schoolCityForFilter}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF}
      .kpfs=${this._kpfs.value}
      .image=${AbschlussLogo}
    >
      ${this._graduationTypeSelection()}
    </anmeldung-component-formular-school-info>`;
  }

  private _getPersonalInformationSection() {
    return html`<anmeldung-component-formular-personal-info
      .form=${this._form}
      .translation=${this._translation}
      .schoolLevel=${'os'}
      .student=${this._student}
      .legalGuardian1AddressDifferent=${this._legalGuardian1AddressDifferent}
      .legalGuardian2AddressDifferent=${this._legalGuardian2AddressDifferent}
      .hide=${['LEGAL_GUARDIAN', 'CONTACT_PREFERENCE', 'NATIONALITY', 'BIRTHDAY']}
    >
    </anmeldung-component-formular-personal-info>`;
  }

  private _getSubjectsSection() {
    return html`<anmeldung-component-formular-graduation-subjects
      .form=${this._form}
      .translation=${this._translation}
      .student=${this._student}
      .subjects=${this._subjectWishes()}
      .bundesland=${this._choosenKPF.value?.school?.bundesland ?? ''}
    ></anmeldung-component-formular-graduation-subjects>`;
  }

  private _getDataValidation() {
    return html`<anmeldung-component-formular-data-validation
      .form=${this._form}
      .student=${this._student.value}
      .translation=${this._translation}
      .schoolLevel=${'graduation'}
      .digitalCoursesChoosen=${[]}
      .schoolName=${this._choosenKPF.value?.school?.name}
      hide-courses
    >
    </anmeldung-component-formular-data-validation>`;
  }

  private _getFinishedScreen() {
    return html`<anmeldung-component-formular-finished
      .form=${this._form}
      .translation=${this._translation}
      .schoolLevel=${'graduation'}
    ></anmeldung-component-formular-finished> `;
  }

  private _getStepTitle() {
    switch (this.step.value) {
      case 0:
        return '...';
      case 1:
        return 'Schulische Daten';
      case 2:
        return 'Persönliche Daten';
      case 3:
        return 'Fächer und zeitliche Verfügbarkeit';
      case 4:
        return 'Datenübersicht';
      case 5:
        return '';
      default:
        return '';
    }
  }

  private _nextDown(rem: number = 1) {
    if (this.step.value > 0) this.step.next(this.step.value - rem);
  }

  private _nextUp(add: number = 1) {
    this.step.next(this.step.value + add);
  }
}
